import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Grid, Divider } from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Business as InterclubIcon,
  TrendingUp as TrendingIcon,
  Link as LinkIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext'; // Import Firebase auth
import { API_BASE_URL } from '../config/environment';

import UserManagement from '../components/UserManagement';
import CompetitionManagement from '../components/CompetitionManagement';
import InterclubManagement from '../components/InterclubManagement';
import Upload from '../components/Upload';
import FileHistory from '../components/FileHistory';
import AdminSettings from '../components/AdminSettings';
import AdminSyncedCoaches from '../components/AdminSyncedCoaches';
import AdminScoreLookup from '../components/AdminScoreLookup';
import StripeConnectAdmin from '../components/StripeConnectAdmin';
import AdminSkaters from '../components/AdminSkaters';
import RoleApprovals from '../components/RoleApprovals';
import Tools from './Tools';

export default function Admin() {
  const { user, isAdmin, adminStatusLoading } = useFirebaseAuth(); // Use Firebase auth
  const navigate = useNavigate();

  // If still loading admin status, show loading
  if (adminStatusLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading admin status...</Typography>
      </Box>
    );
  }

  // If not logged in or not admin, redirect to home
  if (!user || !isAdmin()) {
    navigate('/');
    return null;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', pl: 0 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            🔧 Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users, competitions, files, and system settings.
          </Typography>
        </Box>
      </Paper>

      {/* Admin Routes */}
      <Routes>
        <Route path="/" element={<AdminOverview />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/interclubs" element={<InterclubManagement />} />
        <Route path="/competitions" element={<CompetitionManagement />} />
        <Route path="/skaters" element={<AdminSkaters />} />
        <Route path="/role-approvals" element={<RoleApprovals />} />
        <Route path="/connect" element={<StripeConnectAdmin />} />
        <Route path="/score-lookup" element={<AdminScoreLookup />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/files" element={<FileHistory />} />
        <Route path="/coaches" element={<AdminSyncedCoaches />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/settings" element={<AdminSettings />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Box>
  );
}

// App fee per paid skater
const APP_FEE_PER_SKATER = 5;

// Admin Overview Component
function AdminOverview() {
  const navigate = useNavigate();
  const { getIdToken } = useFirebaseAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getIdToken(true);
      
      // Fetch current season
      const seasonsRes = await fetch(`${API_BASE_URL}/seasons`);
      const seasons = await seasonsRes.json();
      const currentSeason = seasons.find(s => s.isCurrent);
      
      if (!currentSeason) {
        setStats({ noSeason: true });
        return;
      }

      // Fetch all skaters for current season (without interclub filter = all interclubs)
      // Also fetch interclubs to get proper names
      const [skatersRes, interclubsRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/admin/skaters?seasonId=${currentSeason.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(`${API_BASE_URL}/interclubs`),
      ]);
      
      if (!skatersRes.ok) {
        throw new Error('Failed to fetch skaters');
      }
      
      const skatersData = await skatersRes.json();
      const skaters = skatersData.skaters || [];
      const linkStats = skatersData.stats || { usfsVerifiedCount: 0, parentLinkedCount: 0 };

      // Build interclub name map
      const interclubNameMap = {};
      if (interclubsRes.ok) {
        const interclubs = await interclubsRes.json();
        interclubs.forEach(ic => {
          interclubNameMap[ic.id] = ic.name;
        });
      }
      
      // Calculate stats
      const paidCount = skaters.filter(s => s.paid).length;
      const unpaidCount = skaters.filter(s => !s.paid).length;
      const totalRevenue = paidCount * APP_FEE_PER_SKATER;
      
      // Group by interclub for breakdown
      const interclubStats = {};
      skaters.forEach(skater => {
        const icId = skater.interclubId || 'unknown';
        // Use proper interclub name from map, fallback to stored name, then club
        const icName = interclubNameMap[icId] || skater.interclubName || 'Unknown Interclub';
        if (!interclubStats[icId]) {
          interclubStats[icId] = { name: icName, paid: 0, unpaid: 0 };
        }
        if (skater.paid) {
          interclubStats[icId].paid++;
        } else {
          interclubStats[icId].unpaid++;
        }
      });

      setStats({
        seasonName: currentSeason.name,
        totalSkaters: skaters.length,
        paidCount,
        unpaidCount,
        totalRevenue,
        potentialRevenue: skaters.length * APP_FEE_PER_SKATER,
        interclubBreakdown: Object.values(interclubStats).sort((a, b) => b.paid - a.paid),
        usfsVerifiedCount: linkStats.usfsVerifiedCount ?? 0,
        parentUsfsLinkedCount: linkStats.parentLinkedCount ?? 0,
      });
    } catch (err) {
      console.error('Error loading admin stats:', err);
      setStats({ error: true });
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTileClick = (path) => {
    navigate(path);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Welcome to the Admin Dashboard
      </Typography>
      
      {/* Revenue Stats Section */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : stats?.noSeason ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.light' }}>
          <Typography>No current season found. Create a season to see revenue stats.</Typography>
        </Paper>
      ) : stats?.error ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'error.light' }}>
          <Typography>Failed to load stats. Please refresh the page.</Typography>
        </Paper>
      ) : stats && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.9 }}>
            {stats.seasonName} - Platform Revenue
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${stats.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Earned Revenue
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${stats.potentialRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Potential (if all pay)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.paidCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Paid Skaters
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InterclubIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.unpaidCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Unpaid Skaters
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* USFS link metrics */}
          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
          <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.9 }}>
            USFS Account Linking
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.parentUsfsLinkedCount ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Parents USFS Linked
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedUserIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.usfsVerifiedCount ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Skaters USFS Verified
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {stats.interclubBreakdown && stats.interclubBreakdown.length > 0 && (
            <>
              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
              <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.9 }}>
                Revenue by Interclub (${APP_FEE_PER_SKATER}/skater)
              </Typography>
              <Grid container spacing={1}>
                {stats.interclubBreakdown.map((ic, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      borderRadius: 2, 
                      p: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ic.name}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          ${(ic.paid * APP_FEE_PER_SKATER).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {ic.paid} paid / {ic.unpaid} unpaid
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      )}

      <Typography variant="body1" paragraph>
        Use the sidebar navigation to access different administrative functions:
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 3 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/users')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            👥 User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user accounts, roles, and permissions.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/skaters')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Skaters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View season registration status for skaters.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/connect')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            💳 Stripe Connect
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and onboard interclub payment accounts.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/competitions')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            🏆 Competition Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, edit, and manage competitions and seasons.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/score-lookup')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            🔍 Score Lookup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Look up competition points by USFS membership ID.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/interclubs')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            🧭 Interclubs & Clubs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage interclubs, clubs, and their associations.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/upload')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            📤 File Upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and process competition results and documents.
          </Typography>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => handleTileClick('/admin/files')}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            📋 File History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage uploaded files and their processing history.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}